import { useState } from "react";
import styled from "styled-components";

import useAuthenticatedVideoBlob from "../../hooks/useAuthenticatedVideoBlob";

const DebugDetails = styled.details`
  margin-top: 16px;
  border: 1px dashed var(--brand-border, #e3ebec);
  border-radius: 10px;
  padding: 10px 14px;

  & > summary {
    cursor: pointer;
    color: #4b5563;
    font-size: 0.86rem;
    font-weight: 600;
  }
`;

const Hint = styled.p`
  margin: 10px 0 0;
  color: #6b7280;
  font-size: 0.82rem;
  line-height: 1.5;
`;

const ClipRow = styled.div`
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid var(--brand-border, #e3ebec);
`;

const ClipTitle = styled.p`
  margin: 0 0 8px;
  color: var(--brand-ink, #0b1c1e);
  font-size: 0.88rem;
  font-weight: 600;
`;

const Players = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const PlayerBlock = styled.div`
  flex: 1 1 260px;
  min-width: 220px;
  max-width: 420px;

  video {
    width: 100%;
    border-radius: 8px;
    background: #000;
  }
`;

const PlayerLabel = styled.p`
  margin: 0 0 6px;
  color: #4b5563;
  font-size: 0.78rem;
`;

const Muted = styled.p`
  margin: 0;
  color: #6b7280;
  font-size: 0.8rem;
`;

/** @param {unknown} roomType */
const roomLabel = (roomType) =>
  typeof roomType === "string" && roomType ? roomType.replace(/_/g, " ") : "";

/** @param {unknown} status */
const clipStatusLabel = (status) => {
  const labels = /** @type {Record<string, string>} */ ({
    selected: "Selected for generation",
    generating: "Generation in progress",
    generated: "Generated; automated review pending",
    reviewing: "Automated review in progress",
    passed: "Automated check passed",
    uncertain: "Not fully verified",
    rejected: "Flagged by automated review",
    failed: "Generation failed",
  });
  return typeof status === "string" && labels[status]
    ? labels[status]
    : "Review status unavailable";
};

/** @param {{label: string, sourceUrl?: string | null, active: boolean, cacheKey: string}} props */
const ClipPlayer = ({ label, sourceUrl, active, cacheKey }) => {
  const playback = useAuthenticatedVideoBlob({
    sourceUrl: sourceUrl || "",
    active: active && !!sourceUrl,
    cacheKey,
  });
  if (!sourceUrl) return null;
  return (
    <PlayerBlock>
      <PlayerLabel>{label}</PlayerLabel>
      {playback.loading && <Muted>Loading…</Muted>}
      {playback.error && <Muted role="alert">{playback.error}</Muted>}
      {playback.blobUrl && (
        <video
          src={playback.blobUrl}
          controls
          muted
          playsInline
          preload="metadata"
          aria-label={label}
        />
      )}
    </PlayerBlock>
  );
};

/**
 * Owner-only troubleshooting: each generated clip exactly as the provider
 * returned it (uncut) next to its prepared version (trimmed + scaled).
 * Stored media alone does not prove inclusion or approval. Loads lazily.
 * @param {{clips?: Array<Record<string, any>>, cacheKeyBase?: string}} props
 */
const VideoClipDebugList = ({ clips = [], cacheKeyBase = "" }) => {
  const [open, setOpen] = useState(false);
  const rows = (Array.isArray(clips) ? clips : []).filter(
    (clip) => clip && (clip.media_url || clip.raw_media_url)
  );
  if (!rows.length) return null;
  return (
    <DebugDetails
      data-testid="video-clip-debug-list"
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary>Individual clips (troubleshooting)</summary>
      <Hint>
        Compare the generated clip with the trimmed version prepared for the draft. A saved clip or
        an automated pass is not approval to share; verify it against the original listing photos.
      </Hint>
      {rows.map((clip) => {
        const clipKey = clip.id || `ordinal-${clip.ordinal}`;
        const room = roomLabel(clip.room_type);
        return (
          <ClipRow key={clipKey}>
            <ClipTitle>
              Clip {clip.ordinal}
              {room ? ` — ${room}` : ""}
              {` (${clipStatusLabel(clip.status)})`}
            </ClipTitle>
            <Players>
              <ClipPlayer
                label="Generated clip (uncut)"
                sourceUrl={clip.raw_media_url}
                active={open}
                cacheKey={`${cacheKeyBase}:${clipKey}:raw`}
              />
              <ClipPlayer
                label="Prepared clip (trimmed and scaled)"
                sourceUrl={clip.media_url}
                active={open}
                cacheKey={`${cacheKeyBase}:${clipKey}:shipped`}
              />
            </Players>
          </ClipRow>
        );
      })}
    </DebugDetails>
  );
};

export default VideoClipDebugList;
